import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useTabActivity } from '../contexts/TabActivityContext';
import './StagedDialog.css';

interface StagedDialogProps {
  title: string;
  description: string;
  steps: string[];
  step: number;
  onClose: () => void;
  busy?: boolean;
  children: React.ReactNode;
  footer: React.ReactNode;
  className?: string;
  layer?: 'standard' | 'editor';
}

const StagedDialog: React.FC<StagedDialogProps> = ({ title, description, steps, step, onClose, busy = false, children, footer, className = '', layer = 'standard' }) => {
  const titleId = useId();
  const dialog = useRef<HTMLElement>(null);
  const { portalContainer } = useTabActivity();
  useEffect(() => {
    const previous = document.activeElement;
    dialog.current?.focus();
    return () => { if (previous instanceof HTMLElement && previous.isConnected) previous.focus(); };
  }, []);
  return createPortal(<div className={`staged-dialog-backdrop${layer === 'editor' ? ' staged-dialog-over-editor' : ''}`}>
    <section ref={dialog} tabIndex={-1} className={`staged-dialog ${className}`} role="dialog" aria-modal="true" aria-labelledby={titleId}
      onKeyDown={event => {
        if (event.defaultPrevented) return;
        if (event.key === 'Escape') { event.stopPropagation(); if (!busy) onClose(); }
        if (event.key !== 'Tab') return;
        const elements = Array.from(dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), textarea:not(:disabled), select:not(:disabled), [tabindex="0"]') || [])
          .filter(element => !element.closest('[hidden]'));
        const first = elements[0], last = elements[elements.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog.current)) { event.preventDefault(); first?.focus(); }
      }}>
      <header><div><h2 id={titleId}>{title}</h2><p>{description}</p></div><button type="button" className="action-button" disabled={busy} onClick={onClose} aria-label={`Close ${title.toLowerCase()}`}><X size={20} /></button></header>
      <ol className="staged-dialog-steps" aria-label={`${title} steps`}>{steps.map((label, index) => <li key={label} aria-current={index === step ? 'step' : undefined}><span>{index + 1}</span>{label}</li>)}</ol>
      <div className="staged-dialog-body">{children}</div>
      <footer>{footer}</footer>
    </section>
  </div>, portalContainer || document.body);
};

export default StagedDialog;
